import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { XbApiService } from '@merchant-portal/app/features/dashboard/pages/xb/services/xb-api-service.service';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ButtonComponent } from 'ui-component/src/public-api';

@Component({
  selector: 'app-upload-document-dialog',
  standalone: true,
  imports: [CommonModule, ButtonModule, ButtonComponent],
  templateUrl: './upload-document-dialog.component.html',
  styleUrls: ['./upload-document-dialog.component.scss'],
})
export class UploadDocumentDialogComponent implements OnInit {
  uploadedFile: File | null = null;
  isUploading = false;
  uploadError = false;
  errorMessage = '';
  dragActive = false;
  maxUploadSizeMB: number = 20; // default fallback in MB
  isLoadingConfig = true;

  private xbApiService = inject(XbApiService);

  constructor(
    private dialogConfig: DynamicDialogConfig,
    private ref: DynamicDialogRef
  ) {}

  ngOnInit(): void {
    this.loadUploadConfig();
  }

  loadUploadConfig(): void {
    this.xbApiService.getDocumentUploadConfig().subscribe({
      next: response => {
        if (response.data && response.data.maxFileSize) {
          // Convert bytes to MB
          this.maxUploadSizeMB = Math.round(response.data.maxFileSize / (1024 * 1024));
        }
        this.isLoadingConfig = false;
      },
      error: () => {
        // Keep default value 20MB
        this.isLoadingConfig = false;
      },
    });
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragActive = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragActive = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragActive = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFile(files[0]);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFile(input.files[0]);
    }
  }

  handleFile(file: File): void {
    // Validate file type (zip or PDF)
    const allowedTypes = ['application/pdf', 'application/zip', 'application/x-zip-compressed'];
    const maxSizeBytes = this.maxUploadSizeMB * 1024 * 1024;

    if (
      !allowedTypes.includes(file.type) &&
      !file.name.toLowerCase().endsWith('.pdf') &&
      !file.name.toLowerCase().endsWith('.zip')
    ) {
      this.uploadError = true;
      this.errorMessage = `Format zip or PDF, max size ${this.maxUploadSizeMB}mb`;
      return;
    }

    if (file.size > maxSizeBytes) {
      this.uploadError = true;
      this.errorMessage = `File size exceeds ${this.maxUploadSizeMB}MB limit`;
      return;
    }

    this.uploadError = false;
    this.errorMessage = '';
    this.uploadedFile = file;
  }

  removeFile(): void {
    this.uploadedFile = null;
    this.uploadError = false;
    this.errorMessage = '';

    // Clear file input
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  browseFile(): void {
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    fileInput?.click();
  }

  cancel(): void {
    this.ref.close();
  }

  save(): void {
    if (this.uploadedFile) {
      this.ref.close({
        success: true,
        file: this.uploadedFile,
      });
    }
  }
}
